<template>
  <div class="record-container">
    <!-- Parent Record Header -->
    <div class="record-header" @click="toggleMainAccordion">

      <button class="arrow-button desktop">{{ isMainOpen ? '▾' : '▸' }}</button>
      <span class="record-info desktop">
        <strong>{{ record.detaineePhone }}</strong> / 
        <strong>{{ record.detainee }}</strong> / 
        <strong>{{ record.bailAmount }}</strong> / 
        <strong>{{ record.whereJailed }}</strong>
      </span>

      <div class="mobile">

        <div style="display:block;float:left;clear:left;width:230px;">
          <div style="float:right;margin-right:20px;"><strong>{{ record.detaineePhone }} / {{ record.detainee }}</strong></div>
        </div>

        <div style="display:block;clear:both;">
          <div class="timestamp mobile" style="display:block;float:right;clear:both;margin-right:20px;">{{ formatTimestamp(record.timestamp) }}</div>
        </div>
                
        <div style="display:block;clear:right;">

          <button class="arrow-button" style="float:left;clear:left;margin-top:4px;">{{ isMainOpen ? '▾' : '▸' }}</button>

          <div style="float:right;clear:none;margin-top:0px;margin-right:2px"><button class="kebab-menu-mobile" @click.stop="toggleKebabMenu">⋮</button></div>

          <div v-if="record.flag" class="flag-icon mobile" style="float:right;clear:none;">⚑</div>

          <div v-if="record.language == 'es-MX'" class="language-indicator mobile">
            <div class="language-indicator mobile-lang" style="float:right;clear:none;margin-left:-3px;margin-right:10px">ES</div>
          </div>

          <div v-if="record.language == 'ru-RU'" class="language-indicator mobile">
            <div class="language-indicator mobile-lang" style="float:right;clear:none;margin-left:-3px;margin-right:10px">RU</div>
          </div>

          <div class="caller-id" style="float:right;clear:none;">{{ prettyPhone(record.callerId) }}</div>

        </div>

        <div style="display:block;clear:right;width:230px;">
          <div style="white-space:nowrap;float:right;margin-right:20px"><strong>{{ record.bailAmount }}</strong></div>
        </div>

        <div style="display:block;clear:right;width:220px;">
          <div style="display:block;clear:right;float:right;margin-right:12px;overflow:hidden;text-overflow:ellipsis;">
            <strong>{{ record.whereJailed }}</strong>
          </div>
        </div>

        <div style="display:block;float:left;clear:left;width:100%">
          <!-- In-progress specific controls -->
          <template v-if="status === 'in-progress' && record.onHoldTimestamp">
            <div v-if="record.jailCallStatus" class="live-label blink" style="margin:0 auto;text-align:center;margin-top:2px;margin-right:10px">
              {{ displayStatus }}
            </div>
            <button 
              class="talk-now-button" 
              style="margin:0 auto;float:right;margin-top:6px;margin-bottom:4px;margin-right:10px"
              :class="{ 'disabled': talkNowDisabled }"
              :disabled="talkNowDisabled"
              @click.stop="handleTalkNow"
            >
              {{ getTalkNowText() }}
            </button>
          </template>
        </div>

      </div>

      <span class="caller-id desktop">{{ prettyPhone(record.callerId) }}</span>
      <span class="timestamp desktop">{{ formatTimestamp(record.timestamp) }}</span>
      <span v-if="record.flag" class="flag-icon desktop">⚑</span>
      <span v-if="record.language == 'es-MX'" class="language-indicator desktop">
        <span class="desktop-lang">español</span>
      </span>
      <span v-if="record.language == 'ru-RU'" class="language-indicator desktop">
        <span class="desktop-lang">русский</span>
      </span>
      
      <!-- In-progress specific controls -->
      <template v-if="status === 'in-progress' && record.onHoldTimestamp">
        <span v-if="record.jailCallStatus" class="live-label blink desktop">
          {{ displayStatus }}
        </span>
        <button 
          class="talk-now-button desktop" 
          :class="{ 'disabled': talkNowDisabled }"
          :disabled="talkNowDisabled"
          @click.stop="handleTalkNow"
        >
          {{ getTalkNowText() }}
        </button>
      </template>
      
      <!-- Kebab menu for all statuses -->
      <button class="kebab-menu desktop" @click.stop="toggleKebabMenu">⋮</button>

      <div v-if="showKebabMenu" class="kebab-menu-dropdown">
        <button @click.stop="addNote">Add note</button>
        <button @click.stop="toggleFlag">Flag / Unflag</button>
        <button @click.stop="editRecord">Edit record details</button>
        <button @click.stop="addDetaineeCallback">Detainee callback</button>
        <button v-if="status !== 'in-progress'" @click.stop="toggleArchive">
          Archive / Unarchive
        </button>
      </div>

    </div>

    <!-- Expanded Content Container -->
    <div v-if="isMainOpen" class="expanded-content">
      <!-- QA List Record -->
      <div class="child-record">
        <div class="child-record-header" @click="toggleQAList">
          <button class="arrow-button">{{ isQAListOpen ? '▾' : '▸' }}</button>
          <span class="record-type">Intake Q&A</span>
          <span v-if="isOwner" style="color: green; margin-left: 10px;">{{ record.callSid }}</span>
        </div>
        
        <div v-if="isQAListOpen" class="child-record-content">
          <div v-if="record.qaSummary" class="summary-box">
            <div class="summary-title">Summary</div>
            <div class="summary-text">{{ record.qaSummary }}</div>
          </div>
          <div v-for="(qa, index) in record.qaList" :key="index" class="qa-container">
            <div class="question">
              <strong>{{ qa.question }}</strong>
            </div>
            <div class="answer">
              <span>{{ qa.answer }}</span>
              <audio 
                v-if="qa.audioUrl" 
                controls 
                class="answer-audio"
              >
                <source :src="qa.audioUrl" type="audio/mpeg">
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Records -->
      <template v-if="record.records">
        <template v-for="(subRecord, index) in record.records" :key="index">
          <!-- Detainee Callback gets rendered on its own -->
          <detainee-callback-record
            v-if="subRecord.type === 'Detainee Callback'"
            :record="subRecord"
            class="standalone-record"
          />

          <!-- All other record types use the standard header pattern -->
          <div v-else class="child-record">
            <div class="child-record-header" @click="toggleSubRecord(index)">
              <button class="arrow-button">
                {{ isSubRecordOpen(index) ? '▾' : '▸' }}
              </button>
              <span class="record-type">{{ subRecord.type }}</span>
              <span class="record-timestamp">
                {{ formatTimestamp(subRecord.timestamp) }}
              </span>
            </div>
            
            <div v-if="isSubRecordOpen(index)" class="child-record-content">
              <!-- Talk Now Record Type -->
              <talk-now-record
                v-if="subRecord.type === 'Talk Now'"
                :record="subRecord"
                :parent-call-sid="record.callSid"
              />

              <!-- Note Record Type -->
              <div v-else-if="subRecord.type === 'Note'" class="note-container">
                <!-- Note Edit Mode -->
                <div v-if="editingNoteIndex === index">
                  <textarea
                    v-model="editingText"
                    class="note-edit-textarea"
                    rows="3"
                    @keyup.esc="cancelEdit"
                    :ref="`editTextarea-${index}`"
                  ></textarea>
                  <div class="note-edit-actions">
                    <button class="edit-action-button save" @click="saveNote(index)">
                      Save
                    </button>
                    <button class="edit-action-button cancel" @click="cancelEdit">
                      Cancel
                    </button>
                  </div>
                </div>
                <!-- Note View Mode -->
                <div v-else>
                  <div class="note-text-container">
                    <div class="note-text">{{ subRecord.text }}</div>
                    <button class="edit-button" @click="startEdit(index, subRecord.text)">
                      ✎
                    </button>
                  </div>
                  <div class="note-author">{{ subRecord.agent }}</div>
                </div>
              </div>

              <!-- Other Record Types -->
              <div v-else class="note-container">
                <div class="note-text">{{ subRecord.text }}</div>
                <div class="note-author">{{ subRecord.agent }}</div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState, mapGetters } from 'vuex';
import TalkNowRecord from './TalkNowRecord.vue';
import DetaineeCallbackRecord from './DetaineeCallbackRecord.vue';
import { prettyPhone, systemPrompts, getUUID } from '../utils/helpers';

export default {
  name: 'IntakeRecord',
  
  components: {
    TalkNowRecord,
    DetaineeCallbackRecord
  },

  props: {
    record: {
      type: Object,
      required: true
    },
    status: {
      type: String,
      required: true
    },
    talkNowDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isMainOpen: false,
      isQAListOpen: false,
      openSubRecords: new Set(),
      showKebabMenu: false,
      editingNoteIndex: null,
      editingText: '',
      updateTimer: null,
      currentTime: Date.now(),
      addDetaineeCallbackDebounce: null
    };
  },

  computed: {
    ...mapState(['user']),
    ...mapGetters(['userRole', 'getSelectedUser', 'getSelectedCompany']),
    isOwner() {
      return this.userRole === 'Owner';
    },
    displayStatus() {
      if (this.record.jailCallStatus === 'Detainee On Hold' && this.record.onHoldTimestamp) {
        const elapsedSeconds = Math.floor((this.currentTime - new Date(this.record.onHoldTimestamp)) / 1000);
        if (elapsedSeconds >= 90) {
          this.$emit('update:record', {
            ...this.record,
            jailCallStatus: ''
          });
          return '';
        }
        return `${this.record.jailCallStatus} ${elapsedSeconds}s`;
      }
      return this.record.jailCallStatus;
    }
  },

  watch: {
    'record.jailCallStatus': {
      immediate: true,
      handler() {
        if (this.updateTimer) {
          clearInterval(this.updateTimer);
          this.updateTimer = null;
        }
        
        if (this.record.jailCallStatus === 'Detainee On Hold') {
          this.updateTimer = setInterval(() => {
            this.currentTime = Date.now();
          }, 1000);
        }
      }
    }
  },

  methods: {

    prettyPhone,

    async addDetaineeCallback() {
      if (this.addDetaineeCallbackDebounce) {
        return;
      }

      this.addDetaineeCallbackDebounce = setTimeout(() => {
        this.addDetaineeCallbackDebounce = null;
      }, 2000);

      const newRecord = {
        type: "Detainee Callback",
        parentCallSid: this.record.callSid,
        agent: this.getSelectedUser.name,
        timestamp: new Date().toISOString(),
        alert: "Waiting for detainee to call back",
        showAlert: true,
        message: systemPrompts.detaineeCallbackDefaultMessage,
        messageAction: "RECORD_RESPONSE"
      };

      try {
        const response = await axios.put(
          `/portaldb/db/updateDetaineeCallbackRecord/${this.record.callSid}`,
          newRecord
        );

        if (response.data.success) {
          const updatedRecord = {
            ...this.record,
            records: response.data.records
          };
          
          this.$emit('update:record', updatedRecord);
          this.isMainOpen = true;
          this.openSubRecords.add(updatedRecord.records.length - 1);
        }
      } catch (error) {
        console.error('Error adding DetaineeCallback record:', error);
      }

      this.showKebabMenu = false;
    },

    editRecord() {
      this.$emit('edit-record', this.record);
      this.showKebabMenu = false;
    },

    handleOutsideClick(event) {
      if (this.showKebabMenu && !event.target.closest('.kebab-menu-dropdown') && !event.target.closest('.kebab-menu')) {
        this.showKebabMenu = false;
      }
    },

    handleEscKey(event) {
      if (event.key === 'Escape' && this.showKebabMenu) {
        this.showKebabMenu = false;
      }
    },

    async toggleFlag() {
      try {
        const updateObject = {
          flag: !this.record.flag
        };
        
        await axios.put(`/portaldb/db/updateCallPropertiesWithCompany/${this.record.callSid}/${this.record.companyNumber}`, updateObject);
        
        this.$emit('update:record', {
          ...this.record,
          ...updateObject
        });
        
        this.showKebabMenu = false;
      } catch (error) {
        console.error('Error toggling flag:', error);
      }
    },

    addNote() {
      const newNote = {
        type: 'Note',
        timestamp: new Date().toISOString(),
        agent: this.getSelectedUser.name,
        text: '',
        recordId: getUUID()
      };

      const currentRecords = this.record.records || [];
      
      const updatedRecord = {
        ...this.record,
        records: [...currentRecords, newNote]
      };

      this.$emit('update:record', updatedRecord);
      this.isMainOpen = true;
      this.openSubRecords.add(updatedRecord.records.length - 1);
      this.showKebabMenu = false;
    },

    toggleMainAccordion() {
      this.isMainOpen = !this.isMainOpen;
      this.showKebabMenu = false;
    },

    toggleQAList() {
      this.isQAListOpen = !this.isQAListOpen;
    },

    toggleSubRecord(index) {
      if (this.openSubRecords.has(index)) {
        this.openSubRecords.delete(index);
      } else {
        this.openSubRecords.add(index);
      }
    },

    isSubRecordOpen(index) {
      return this.openSubRecords.has(index);
    },

    toggleKebabMenu() {
      document.dispatchEvent(new Event('close-all-kebabs'));
      this.showKebabMenu = !this.showKebabMenu;
    },

    handleTalkNow() {
      this.$emit('talk-now', this.record);
    },

    toggleArchive() {
      const newStatus = this.status === 'archived' ? 'completed' : 'archived';
      this.$emit('archive', this.record.callSid, this.record.companyNumber, newStatus);
      this.showKebabMenu = false;
    },

    getTalkNowText() {
      if (this.record.agentOnCall) {
        const strA = this.record.agentOnCall.split(' ');
        return "Talking to Agent " + strA[strA.length-1];
      }
      return "Talk Now";
    },

    formatTimestamp(timestamp) {
      const diffInSeconds = (Date.now() - new Date(timestamp)) / 1000;
      if (diffInSeconds < 60) {
        return 'just now';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return hours === 1 ? 'an hour ago' : `${hours} hours ago`;
      } else if (diffInSeconds < 172800) {
        return 'yesterday';
      } else {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} days ago`;
      }
    },

    startEdit(index, text) {
      this.editingNoteIndex = index;
      this.editingText = text;
      this.$nextTick(() => {
        const refKey = `editTextarea-${index}`;
        const textarea = this.$refs[refKey];
        if (textarea && Array.isArray(textarea)) {
          textarea[0].focus();
        } else if (textarea) {
          textarea.focus();
        }
      });
    },

    async saveNote(index) {
      try {
        const updatedNote = {
          ...this.record.records[index],
          text: this.editingText
        };

        await axios.put(`/portaldb/db/updateCallPropertiesWithCompany/${this.record.callSid}/${this.record.companyNumber}`, {
          records: this.record.records.map((record, idx) => 
            idx === index ? updatedNote : record
          )
        });

        this.$emit('update:record', {
          ...this.record,
          records: this.record.records.map((record, idx) =>
            idx === index ? updatedNote : record
          )
        });

        this.editingNoteIndex = null;
        this.editingText = '';
      } catch (error) {
        console.error('Error saving note:', error);
        this.$emit('error', 'Failed to save note');
      }
    },

    cancelEdit() {
      this.editingNoteIndex = null;
      this.editingText = '';
    }
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleEscKey);
    document.addEventListener('close-all-kebabs', () => this.showKebabMenu = false);    
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscKey);
    document.removeEventListener('close-all-kebabs', () => this.showKebabMenu = false);
    
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this.updateTimer = null;
    }
  }
};
</script>

<style scoped>

.caller-id {
  color: gray;
  margin-right: var(--caller-timestamp-gap);
}

.mobile {
  display: none;
}

.summary-box {
  background-color: #fff;
  border-left: 4px solid #8b4513;
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.summary-title {
  font-weight: 600;
  color: #8b4513;
  color: #333333;
  margin-bottom: 8px;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-text {
  line-height: 1.4;
  color: #333;
}

.mobile-lang {
  display: none;
}

.record-container {
  border: 1px solid #d2b48c;
  margin-bottom: 10px;
  border-radius: 4px;
  --caller-timestamp-gap: 24px;
  --caller-timestamp-gap-mobile: 8px;
}

.record-header {
  background-color: #f5f5dc;
  padding: 2px;
  padding-left: 10px;
  padding-right: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
}

.expanded-content {
  padding: 10px;
  background-color: #ffffe0;
}

.child-record {
  border: 1px solid #d2b48c;
  margin-bottom: 8px;
  border-radius: 4px;
}

.child-record:last-child {
  margin-bottom: 0;
}

.child-record-header {
  background-color: #f8f8f0;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.child-record-content {
  padding: 10px;
  background-color: white;
}

.record-type {
  font-weight: 500;
  margin-right: 10px;
}

.record-timestamp {
  color: #666;
  font-size: 0.9em;
}

.arrow-button {
  color: #8b4513;
  font-size: 18px;
  margin-right: 4px;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  display: inline-block;
  transform: scale(2.0);
  transform-origin: center;
  line-height: 1;
  vertical-align: middle;
}

.arrow-button:focus-visible {
  outline: 2px solid #8b4513;
  outline-offset: 2px;
}

.arrow-button:active,
.arrow-button:focus {
  color: #8b4513;
  background-color: transparent;
}

.record-info {
  flex-grow: 1;
}

.timestamp {
  color: gray;
  margin-right: 10px;
}

.language-indicator {
  color: #6495ED;
  margin-right: 10px;
  font-style: italic;
}

.flag-icon {
  color: #dc3545;
  font-size: 24px;
  margin: 0 8px;
  display: flex;
  align-items: center;
}

.live-label {
  padding: 5px 10px;
  border: 2px solid #d2b48c;
  background-color: #f5f5dc;
  border-radius: 10px;
  font-weight: bold;
  color: red;
  margin-left: 10px;
}

.talk-now-button {
  margin-left: 10px;
  margin-right: 10px;
  padding: 5px 10px;
  border: 2px solid #8b4513;
  background-color: #d2b48c;
  cursor: pointer;
  border-radius: 5px;
  font-weight: bold;
  color: white;
}

.talk-now-button.disabled {
  opacity: .33;
}

.kebab-menu {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #8b4513;
  margin-left: 6px;
  padding: 8px 12px;
  outline: none;
  flex-shrink: 0;
}

.kebab-menu:focus {
  outline: none;
}

.kebab-menu-dropdown {
  position: absolute;
  background-color: white;
  border: 1px solid #d2b48c;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 5px 10px;
  z-index: 100;
  right: 0;
  top: 100%;
}

.kebab-menu-dropdown button {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  background: none;
  border: none;
  cursor: pointer;
}

.kebab-menu-dropdown button:hover {
  background-color: #f5f5dc;
}

.qa-container {
  margin-bottom: 10px;
}

.question {
  color: #333333;
  line-height: 1.2em;
  margin-bottom: 4px;
}

.answer {
  margin-left: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.answer-audio {
  height: 30px;
  max-width: 250px;
  min-width: 250px;
}

.note-container {
  padding: 10px;
}

.note-text {
  margin-bottom: 8px;
  line-height: 1.4;
}

.note-author {
  color: #666;
  font-style: italic;
}

.blink {
  animation: blink 2s linear infinite;
}

@keyframes blink {
  0% { opacity: 1; }
  50% { opacity: 0; }
  100% { opacity: 1; }
}

.note-edit-textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  resize: vertical;
  margin-bottom: 8px;
}

.note-edit-textarea:focus {
  outline: none;
  border-color: #8b4513;
  box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
}

.note-text-container {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 4px;
}

.edit-button {
  background: none;
  border: none;
  font-size: 16px;
  color: #8b4513;
  cursor: pointer;
  opacity: 0.5;
  padding: 4px;
  transition: opacity 0.2s ease;
}

.edit-button:hover {
  opacity: 1;
}

.note-edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.edit-action-button {
  padding: 4px 12px;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.edit-action-button.save {
  background-color: #d2b48c;
  color: white;
}

.edit-action-button.save:hover {
  background-color: #8b4513;
  border-color: #8b4513;
}

.edit-action-button.cancel {
  background-color: white;
  color: #666;
}

.edit-action-button.cancel:hover {
  background-color: #f5f5f5;
}

@media (max-width: 600px) {

  .caller-id {
    margin-right: var(--caller-timestamp-gap-mobile);
  }

  .kebab-menu-mobile {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    color: #8b4513;
    margin-left: 6px;
    outline: none;
  }

  .desktop {
    display:none;
  }

  .mobile {
    display: block;
  }

  .answer {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .answer-audio {
    width: 100%;
    min-width: 190px;
    max-width: 190px;
  }

  .record-header {
    padding-right: 5px;
  }

  .flag-icon {
    margin: -6px 3px -15px 0px;
  }

  .desktop-lang {
    display: none;
  }
  
  .mobile-lang {
    display: inline;
  }

  .talk-now-button {
    margin-right: 5px;
  }
}
</style>
